import { BookOpen, Mail, ShieldCheck, X, Sparkles } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

// The "bribe" modal shown on exit-intent after a partial (Step-1) lead capture.
// Reinforces that the roadmap is already in motion so the prospect doesn't feel
// they've wasted the entry — and re-anchors on the email they just provided.
export default function ExitIntentModal({ open, onClose, contact, businessName }) {
  const contactLabel = contact || "the address you provided";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="bg-[#0A1122] border-gold/50 text-white max-w-lg overflow-hidden"
        data-testid="exit-intent-modal"
        style={{
          boxShadow:
            "0 30px 80px rgba(0,0,0,0.6), 0 0 60px rgba(212,175,55,0.25)",
        }}
      >
        <div
          className="absolute inset-x-0 top-0 h-px"
          style={{
            background:
              "linear-gradient(90deg, transparent, rgba(212,175,55,0.9), transparent)",
          }}
        />

        <DialogHeader className="text-center sm:text-left">
          <div className="flex items-center gap-2 mb-2 text-[10px] uppercase tracking-[0.32em] text-gold justify-center sm:justify-start">
            <Sparkles size={11} /> Alfred · Field Note
          </div>
          <DialogTitle
            className="font-display font-light text-2xl sm:text-3xl text-white leading-tight"
            data-testid="exit-intent-headline"
          >
            Wait! Your Roadmap is almost ready.
          </DialogTitle>
          <DialogDescription
            className="text-slate-300 text-sm leading-relaxed pt-3"
            data-testid="exit-intent-subtext"
          >
            You&apos;ve already provided your business details. Even if you
            don&apos;t complete the full assessment now, Alfred is currently
            analyzing your website. We will email your{" "}
            <span className="text-gold font-display">
              Custom 2026 Growth Roadmap
            </span>{" "}
            to <span className="text-white">{contactLabel}</span> within{" "}
            <span className="text-gold">10 minutes</span>.
          </DialogDescription>
        </DialogHeader>

        {/* E-book thumbnails */}
        <div className="grid grid-cols-2 gap-3 my-4" data-testid="exit-intent-ebooks">
          <EbookThumb
            title="Local Dominance"
            subtitle="The 2026 Playbook"
            accent="#D4AF37"
            testId="ebook-local-dominance"
          />
          <EbookThumb
            title="Divine Stewardship"
            subtitle="Owner's Field Guide"
            accent="#60A5FA"
            testId="ebook-divine-stewardship"
          />
        </div>

        {/* Reassurance strip */}
        <div
          className="rounded-sm border p-3 flex items-start gap-3 text-[12px] text-slate-300"
          style={{
            borderColor: "rgba(52,211,153,0.35)",
            background: "rgba(52,211,153,0.06)",
          }}
        >
          <ShieldCheck size={14} className="text-emerald-400 shrink-0 mt-0.5" />
          <div>
            <div className="text-emerald-400 uppercase tracking-[0.2em] text-[9px] mb-0.5">
              Partial lead saved
            </div>
            {businessName ? (
              <>Your entry for <span className="text-white">{businessName}</span> is already in the queue with John &amp; Parker.</>
            ) : (
              <>Your entry is already in the queue with John &amp; Parker.</>
            )}
          </div>
        </div>

        <div className="mt-4 flex flex-col sm:flex-row gap-2">
          <button
            type="button"
            onClick={onClose}
            className="btn-gold flex-1 py-3 rounded-sm text-sm inline-flex items-center justify-center gap-2 min-h-[48px]"
            data-testid="exit-intent-continue"
          >
            <Mail size={14} /> Keep My Roadmap Coming
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-3 rounded-sm text-xs text-slate-400 hover:text-white inline-flex items-center justify-center gap-1 min-h-[48px]"
            data-testid="exit-intent-dismiss"
          >
            <X size={12} /> Dismiss
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EbookThumb({ title, subtitle, accent, testId }) {
  return (
    <div
      className="relative rounded-sm border p-3 overflow-hidden"
      style={{
        borderColor: `${accent}55`,
        background: `linear-gradient(180deg, ${accent}18 0%, rgba(6,11,20,0.85) 100%)`,
        boxShadow: `0 8px 24px -12px ${accent}55`,
      }}
      data-testid={testId}
    >
      <div
        className="absolute inset-x-0 top-0 h-px"
        style={{
          background: `linear-gradient(90deg, transparent, ${accent}, transparent)`,
        }}
      />
      <div className="flex items-start gap-2">
        <div
          className="w-9 h-11 rounded-sm border flex items-center justify-center shrink-0"
          style={{
            borderColor: `${accent}66`,
            background: "rgba(6,11,20,0.6)",
          }}
        >
          <BookOpen size={16} style={{ color: accent }} />
        </div>
        <div className="min-w-0">
          <div
            className="text-[9px] uppercase tracking-[0.22em] mb-0.5"
            style={{ color: accent }}
          >
            E-Book
          </div>
          <div className="font-display text-sm text-white leading-tight truncate">
            {title}
          </div>
          <div className="text-[10px] text-slate-500 truncate">{subtitle}</div>
        </div>
      </div>
    </div>
  );
}
